<template>
  <div id="app">
    <!-- 設備檢測提示 - 只在非移動設備上顯示 -->
    <div v-if="!isMobileOrTablet" class="device-warning">
      <div class="warning-content">
        <div class="warning-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none"
               stroke="#FF9E15" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
            <line x1="12" y1="18" x2="12" y2="18.01"></line>
          </svg>
        </div>
        <h2 class="chinese-text">請使用手機或平板進行操作</h2>
        <p class="chinese-text">為了提供最佳的簽署體驗，本系統僅支持在移動設備上使用。</p>
        <div class="qr-code-container">
          <!-- 修改QR碼容器確保完美居中且無內邊距 -->
          <div class="qr-code">
            <canvas ref="qrCanvas" width="180" height="180"></canvas>
          </div>
          <p class="chinese-text">掃描此二維碼在移動設備上開啟</p>
        </div>
      </div>
    </div>

    <!-- 主應用界面 - 只在移動設備上顯示 -->
    <div v-if="isMobileOrTablet" class="app-container">
      <header>
        <div class="header-container">
          <div class="logo-container">
            <img alt="ProActive Asset 標誌" src="@/assets/logo.webp" class="logo">
            <div class="company-name">{{ companyName }}</div>
          </div>
        </div>
      </header>
      <main>
        <router-view></router-view>
      </main>
      <footer>
        <!-- 添加忠告警示信息 -->
        <div class="warning">
          <div class="warning-text chinese-text">忠告: 借錢梗要還，咪俾錢中介</div>
        </div>
        <!-- 修改為灰色背景 -->
        <div class="copyright-section">
          <div class="copyright">Powered by Foundy Solution Limited</div>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import {ref, onMounted, onBeforeUnmount, nextTick} from 'vue';

export default {
  name: 'App',
  setup() {
    const companyName = process.env.VUE_APP_COMPANY_NAME || 'ProActive Asset';
    const isMobileOrTablet = ref(false); // 默認為false，避免頁面內容被加載
    const qrCanvas = ref(null);

    // 生成QR碼的函數 (使用 qrious 庫，確保填滿整個canvas)
    // Generate QR code - simple and direct approach
    const generateQRCode = () => {
      if (!qrCanvas.value) return;

      const currentUrl = window.location.href;

      // Simple implementation with QRCode.js which has better default sizing
      import('qrcode')
          .then((QRCode) => {
            // Clear any existing content
            const ctx = qrCanvas.value.getContext('2d');
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, 180, 180);

            // Generate QR code directly to fill the canvas
            QRCode.toCanvas(qrCanvas.value, currentUrl, {
              width: 180,
              margin: 0,  // No margin
              color: {
                dark: '#000000',
                light: '#FFFFFF'
              }
            });
          })
          .catch(error => {
            console.warn('Failed to load QR code library:', error);
            // Fallback to a simple message
            const ctx = qrCanvas.value.getContext('2d');
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, 180, 180);
            ctx.font = '14px "Microsoft JhengHei", sans-serif';
            ctx.fillStyle = '#000000';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('請掃描使用手機訪問', 90, 80);
            ctx.fillText(window.location.origin, 90, 100);
          });
    };

    // 更安全的設備檢測函數
    const detectDevice = () => {
      // 使用多種方法檢測移動設備
      const userAgent = navigator.userAgent.toLowerCase();

      // 檢測常見移動設備用戶代理
      const isMobile = /iphone|ipod|android|blackberry|opera mini|iemobile|mobile/i.test(userAgent);

      // 檢測平板設備
      const isTablet = /ipad|tablet|kindle|playbook/i.test(userAgent) ||
          (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

      // 檢測屏幕尺寸（通常移動設備較小）
      const isSmallScreen = window.innerWidth <= 1024 && window.innerHeight <= 1366;

      // 檢測觸摸功能
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

      // 檢測設備方向感應器（典型的移動特性）
      const hasOrientation = typeof window.orientation !== 'undefined' ||
          userAgent.indexOf('mobile') !== -1;

      // 綜合多種特徵進行判斷
      isMobileOrTablet.value = isMobile || isTablet || (isSmallScreen && hasTouch) || hasOrientation;

      // 如果不是移動設備，鎖定頁面滾動
      if (!isMobileOrTablet.value) {
        document.body.classList.add('no-scroll');

        // 僅在非移動設備時生成QR碼
        nextTick(() => {
          generateQRCode();
        });
      } else {
        document.body.classList.remove('no-scroll');
      }
    };

    // Function to set correct viewport height for mobile
    const setVhVariable = () => {
      // First we get the viewport height and we multiply it by 1% to get a value for a vh unit
      const vh = window.innerHeight * 0.01;
      // Then we set the value in the --vh custom property to the root of the document
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    onMounted(() => {
      // 初始檢測
      detectDevice();

      // Set the --vh variable for better mobile height
      setVhVariable();

      // 監聽窗口大小變化
      window.addEventListener('resize', detectDevice);
      window.addEventListener('resize', setVhVariable);

      // 監聽設備方向變化
      window.addEventListener('orientationchange', detectDevice);
      window.addEventListener('orientationchange', setVhVariable);

      // 防止F12開發者工具繞過檢測
      const mutationCallback = (mutationsList) => {
        for (const mutation of mutationsList) {
          if (mutation.type === 'attributes' &&
              (mutation.target.classList.contains('device-warning') ||
                  mutation.target.classList.contains('app-container'))) {
            // 如果檢測到DOM篡改，重新應用檢測
            detectDevice();
          }
        }
      };

      const observer = new MutationObserver(mutationCallback);
      observer.observe(document.body, {attributes: true, childList: true, subtree: true});
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', detectDevice);
      window.removeEventListener('resize', setVhVariable);
      window.removeEventListener('orientationchange', detectDevice);
      window.removeEventListener('orientationchange', setVhVariable);
    });

    return {
      companyName,
      isMobileOrTablet,
      qrCanvas
    };
  }
}
</script>

<style>
:root {
  /* ProActive Asset 品牌色調 */
  --primary-color: #FF9E15; /* 主品牌橙色 */
  --primary-dark: #333333; /* 深色文本 */
  --primary-light: #ffffff; /* 白色文本 */
  --light-gray: #f7f7f7; /* 淺灰背景 */
  --border-gray: #e0e0e0; /* 邊框顏色 */
  --accent-gray: #666666; /* 輔助文本顏色 */
  --shadow: rgba(0, 0, 0, 0.1); /* 陰影顏色 */
  --text-shadow: rgba(0, 0, 0, 0.2); /* 文字陰影 */
  --footer-gray: #f2f2f2; /* 頁腳灰色 */
}

html {
  height: -webkit-fill-available;
}

body {
  min-height: 100vh;
  min-height: -webkit-fill-available;
  margin: 0;
  padding: 0;
  overflow-x: hidden; /* Prevent horizontal scrolling */
}

body.no-scroll {
  overflow: hidden;
}

#app {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Microsoft JhengHei", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--primary-dark);
  min-height: 100vh;
  background-color: var(--light-gray);
}

/* 專門針對中文文字的樣式 */
.chinese-text {
  font-family: "Microsoft JhengHei", sans-serif !important;
  letter-spacing: 0.05em;
}

.app-container {
  min-height: 100vh; /* Fallback */
  min-height: -webkit-fill-available;
  min-height: calc(var(--vh, 1vh) * 100);
  display: flex;
  flex-direction: column;
  overflow-y: auto; /* Allow content to scroll */
  position: relative; /* For proper positioning */
}

header {
  height: 70px;
  background-color: var(--primary-color);
  box-shadow: 0 2px 4px var(--shadow);
  position: relative;
  z-index: 10;
}

.header-container {
  max-width: 1200px;
  height: 100%;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
}

.logo-container {
  display: flex;
  align-items: center;
  gap: 25px;
}

.logo {
  height: 42px;
  width: auto;
}

.company-name {
  color: var(--primary-light);
  font-size: 1.25rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-shadow: 0 1px 2px var(--text-shadow);
}

main {
  flex: 1;
  padding: 40px 20px;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  background-color: transparent;
  overflow-y: auto; /* Enable scrolling in main content area */
}

footer {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: auto;
}

/* 新添加的忠告警示樣式 */
.warning {
  background-color: var(--primary-color);
  padding: 15px 0;
  text-align: center;
  width: 100%;
}

.warning-text {
  color: var(--primary-light);
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-shadow: 0 1px 2px var(--text-shadow);
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* 修改為灰色背景 */
.copyright-section {
  background-color: var(--footer-gray);
  padding: 8px 0;
  text-align: center;
  width: 100%;
}

.copyright {
  color: var(--accent-gray); /* 改回深灰色，在灰色背景上 */
  font-size: 0.7rem;
  letter-spacing: 0.03em;
  font-weight: 400;
  max-width: 1200px;
  margin: 0 auto;
}

/* 設備警告樣式 - 更新為更接近圖片的樣式 */
.device-warning {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  backdrop-filter: blur(5px);
}

.warning-content {
  background-color: white;
  border-radius: 12px;
  padding: 40px 30px;
  max-width: 450px;
  width: 100%;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  animation: fadeIn 0.5s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.warning-icon {
  color: var(--primary-color);
  margin: 0 auto 25px;
  animation: pulse 2s infinite ease-in-out;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.warning-icon svg {
  stroke: var(--primary-color);
}

.warning-content h2 {
  color: var(--primary-dark);
  margin-bottom: 15px;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: 0.05em;
}

.warning-content p {
  color: var(--accent-gray);
  margin-bottom: 25px;
  line-height: 1.5;
  font-size: 15px;
  letter-spacing: 0.03em;
}

/* 新的QR碼容器樣式 - 改進以確保QR碼無邊距填滿容器 */
.qr-code-container {
  margin: 25px 0 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.qr-code {
  margin: 0 auto 15px;
  width: 180px;
  height: 180px;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  padding: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: relative;
}

.qr-code canvas {
  display: block;
  width: 100%;
  height: 100%;
  image-rendering: pixelated;
  object-fit: none;
  object-position: center;
}

.qr-code-container p {
  font-size: 14px;
  font-weight: 500;
  color: var(--primary-dark);
  margin: 12px 0 0;
  line-height: 1.3;
  letter-spacing: 0.04em;
}

/* 按鈕樣式 - 修改簽名按鈕顏色 */
button.primary {
  background-color: var(--primary-color);
  color: var(--primary-light);
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.03em;
  text-shadow: 0 1px 1px var(--text-shadow);
  box-shadow: 0 2px 6px rgba(255, 158, 21, 0.3);
}

button.primary:hover {
  background-color: #ed8c00;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(255, 158, 21, 0.4);
}

button.primary:active {
  transform: translateY(0);
  box-shadow: 0 1px 3px rgba(255, 158, 21, 0.3);
}

/* 簽名按鈕 - 特別樣式 */
button.sign-button {
  background-color: #2E8B57; /* 海綠色 */
  color: white;
  border: none;
  padding: 14px 28px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.05em;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  box-shadow: 0 2px 8px rgba(46, 139, 87, 0.4);
  display: block;
  width: 100%;
  text-align: center;
  margin: 15px 0;
}

button.sign-button:hover {
  background-color: #267349; /* 深一點的綠色 */
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(46, 139, 87, 0.5);
}

button.sign-button:active {
  transform: translateY(0);
  background-color: #1e5d3a;
  box-shadow: 0 1px 4px rgba(46, 139, 87, 0.3);
}

button.secondary {
  background-color: transparent;
  color: var(--primary-color);
  border: 1px solid var(--primary-color);
  padding: 11px 23px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.03em;
}

button.secondary:hover {
  background-color: rgba(255, 158, 21, 0.1);
  transform: translateY(-1px);
}

button.secondary:active {
  transform: translateY(0);
  background-color: rgba(255, 158, 21, 0.15);
}

/* 表單元素樣式 */
input, select, textarea {
  border: 1px solid var(--border-gray);
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 16px;
  width: 100%;
  transition: border-color 0.2s, box-shadow 0.2s;
  letter-spacing: 0.02em;
}

input:focus, select:focus, textarea:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(255, 158, 21, 0.2);
  outline: none;
}

/* 卡片樣式 */
.card {
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px var(--shadow);
  padding: 24px;
  margin-bottom: 20px;
  transition: box-shadow 0.3s ease, transform 0.3s ease;
}

.card:hover {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

/* 全局樣式重置 */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

body {
  margin: 0;
  padding: 0;
}

/* 標題元素統一增加字距 */
h1, h2, h3, h4, h5, h6 {
  letter-spacing: 0.04em;
}

/* 響應式設計 - 移動優化 */
@media (max-width: 768px) {
  header {
    height: 60px;
  }

  .company-name {
    font-size: 1.1rem;
  }

  .logo {
    height: 36px;
  }

  main {
    padding: 20px 15px;
  }

  .card {
    padding: 20px 16px;
    border-radius: 8px;
  }

  input, select, textarea {
    font-size: 16px;
    padding: 12px 16px;
  }

  button.primary, button.secondary {
    width: 100%;
    padding: 12px 24px;
    font-size: 16px;
    letter-spacing: 0.04em;
  }

  button.sign-button {
    padding: 14px 24px;
  }

  .warning-text {
    font-size: 1rem;
    padding: 0 15px;
  }
}

@media (max-width: 480px) {
  .logo {
    height: 40px;
  }

  .company-name {
    letter-spacing: 0.06em;
    font-size: 1rem;
  }

  .platform-name {
    font-size: 0.8em;
  }

  .logo-container {
    gap: 15px;
  }

  header {
    height: 56px;
  }

  main {
    padding: 16px 12px;
  }

  .card {
    margin-bottom: 16px;
    padding: 16px;
  }

  h1 {
    font-size: 1.5rem;
  }

  h2 {
    font-size: 1.25rem;
  }

  .warning-content {
    padding: 30px 20px;
  }

  .qr-code {
    width: 160px;
    height: 160px;
  }

  .qr-code canvas {
    width: 100%;
    height: 100%;
  }

  .chinese-text {
    letter-spacing: 0.05em;
  }

  .warning-text {
    font-size: 1.3rem;
    letter-spacing: 0.05em;
  }

  button.sign-button {
    padding: 12px 20px;
    font-size: 15px;
  }
}
</style>